import Link from 'next/link';
import { IconArrowRight } from '@/components/icons/IconArrowRight';
import type { HeadingLevel } from '@/lib/utils/headingLevel';
import { cn } from '@/lib/utils/tw-merge';
import type { VehicleCardModel } from './vehicle.types';
import { VehicleEnvkvLine } from './VehicleEnvkvLine';
import { VehicleMedia } from './VehicleMedia';
import { VehiclePriceBlock } from './VehiclePriceBlock';
import { VehicleSpecs } from './VehicleSpecs';

type VehicleCardProps = {
    vehicle: VehicleCardModel;
    sizes: string;
    headingLevel?: HeadingLevel;
    eager?: boolean;
    className?: string;
};

/**
 * One vehicle as a card, linking to its detail page. Its six parts are direct
 * children, so a card grid can line them up row by row (subgrid, vehicle-card.css).
 */
export function VehicleCard({ vehicle, sizes, headingLevel = 3, eager = false, className }: VehicleCardProps) {
    const Heading = `h${headingLevel}` as const;

    return (
        <Link href={vehicle.href} className={cn('vehicle-card', className)}>
            <VehicleMedia
                variant="card"
                image={vehicle.image}
                sizes={sizes}
                badges={vehicle.badges}
                imageCount={vehicle.imageCount}
                availability={vehicle.availability}
                eager={eager}
            />

            <Heading className="vehicle-card__title card-title">{vehicle.title}</Heading>
            <p className="vehicle-card__subtitle text-sm">{vehicle.subtitle}</p>

            <VehiclePriceBlock
                className="vehicle-card__price"
                priceText={vehicle.priceText}
                financeNote={vehicle.financeNote}
            />

            <VehicleSpecs className="vehicle-card__tech" specs={vehicle.specs} />

            <div className="vehicle-card__foot">
                <VehicleEnvkvLine className="vehicle-card__envkv text-sm" parts={vehicle.envkvParts} />

                <span className="vehicle-card__cta label-sm">
                    Details
                    <IconArrowRight width={15} height={15} aria-hidden="true" />
                </span>
            </div>
        </Link>
    );
}
